import { useState } from 'react';
import { Plus, Minus, X, Users, Radio } from 'lucide-react';
import {
  APPLIANCE_TYPES,
  APPLIANCE_REFERENCE,
  RESOURCE_STATUSES,
  RESOURCE_STATUS_LABELS,
  summariseCapability,
  baOperatorRequirement,
  rosterBAOperators,
  rosterCrewCount,
  activeMembers,
  createUnit,
  type Incident,
  type ApplianceType,
  type ResourceStatus,
  type ResourceUnit,
  type MemberRoster,
  type CreateUnitInput,
  type BrigadeStation,
} from '@duty/shared';
import { useIncidentStore } from '@/incident/store';
import { Field, TextInput, type Option } from '@/components/ui/controls';
import { StationPicker } from '@/components/ui/StationPicker';
import { allStations } from '@/incident/coverage';

const typeOpts: Option<ApplianceType>[] = APPLIANCE_TYPES.map((t) => ({
  value: t,
  label: APPLIANCE_REFERENCE[t].label,
}));

/** Responding resources with a live capability summary feeding the rules. */
export function ResourcesPanel({ incident }: { incident: Incident }) {
  const { dispatch, config, roster } = useIncidentStore();
  const cap = summariseCapability(incident.resources, config.water.bulkWaterMinL);
  const req = baOperatorRequirement(config);
  const rosterBA = roster ? rosterBAOperators(roster) : null;
  const displayBA = rosterBA ?? cap.baOperators;
  const baShort = displayBA < req.min;
  const rosterCrew = roster ? rosterCrewCount(roster) : null;
  const displayCrew = rosterCrew ?? cap.crewCount;

  return (
    <div className="space-y-3">
      {/* Feed status */}
      {roster && <FeedStatusBadge roster={roster} />}

      {/* Capability summary */}
      <div className="flex flex-wrap gap-1.5">
        <SummaryChip
          label="Crew"
          value={String(displayCrew)}
          tone={displayCrew === 0 ? 'warn' : 'ok'}
          detail={roster ? 'from roster' : undefined}
        />
        <SummaryChip
          label="BA operators"
          value={`${displayBA}/${req.min}`}
          tone={baShort ? 'warn' : 'ok'}
          detail={roster ? 'from roster' : undefined}
        />
        <SummaryChip label="Pumpers" value={String(cap.pumpers)} tone={cap.pumpers ? 'ok' : 'warn'} />
        <SummaryChip label="Onboard" value={`${cap.onboardWaterL} L`} />
        <SummaryChip label="Bulk water" value={cap.hasBulkWater ? 'yes' : 'no'} tone={cap.hasBulkWater ? 'ok' : undefined} />
      </div>

      {/* Units */}
      <ul className="space-y-1.5">
        {incident.resources.map((u) => (
          <UnitRow key={u.id} unit={u} />
        ))}
        {incident.resources.length === 0 && (
          <li className="rounded-md border border-dashed border-border p-2 text-xs text-muted">
            No responding units yet.
          </li>
        )}
      </ul>

      <AddUnitForm onAdd={(input) => dispatch({ type: 'add-unit', unit: createUnit(input) })} />
    </div>
  );
}

function UnitRow({ unit }: { unit: ResourceUnit }) {
  const { dispatch } = useIncidentStore();
  return (
    <li className="rounded-md border border-border bg-surface-2/60 p-2">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium">{unit.callsign}</span>
            <span className="chip">{unit.agency}</span>
          </div>
          <div className="text-[11px] text-muted">
            {APPLIANCE_REFERENCE[unit.type].label} · {unit.onboardWaterL} L
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <select
            aria-label={`${unit.callsign} status`}
            value={unit.status}
            onChange={(e) =>
              dispatch({ type: 'update-unit', unitId: unit.id, patch: { status: e.target.value as ResourceStatus } })
            }
            className="rounded border border-border bg-surface px-1 py-0.5 text-[11px]"
          >
            {RESOURCE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {RESOURCE_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          <button
            aria-label={`Remove ${unit.callsign}`}
            onClick={() => dispatch({ type: 'remove-unit', unitId: unit.id })}
            className="rounded p-1 text-muted hover:text-crit"
          >
            <X size={13} />
          </button>
        </div>
      </div>
      <div className="mt-1.5 flex items-center gap-3 text-[11px] text-muted">
        <Stepper
          icon={<Users size={11} />}
          label="Crew"
          value={unit.crewCount}
          onChange={(v) => {
            const crewCount = Math.max(0, v);
            // BA operators can never exceed total crew aboard.
            const baOperators = Math.min(unit.baOperators, crewCount);
            dispatch({ type: 'update-unit', unitId: unit.id, patch: { crewCount, baOperators } });
          }}
        />
        <Stepper
          label="BA"
          value={unit.baOperators}
          onChange={(v) => {
            const baOperators = Math.min(Math.max(0, v), unit.crewCount);
            dispatch({ type: 'update-unit', unitId: unit.id, patch: { baOperators } });
          }}
        />
      </div>
    </li>
  );
}

function Stepper({
  label,
  value,
  onChange,
  icon,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  icon?: React.ReactNode;
}) {
  return (
    <span className="inline-flex items-center gap-1">
      {icon}
      {label}
      <button
        aria-label={`decrease ${label}`}
        onClick={() => onChange(Math.max(0, value - 1))}
        className="rounded border border-border px-1 hover:text-accent"
      >
        <Minus size={10} />
      </button>
      <span className="w-3 text-center font-mono text-text">{value}</span>
      <button
        aria-label={`increase ${label}`}
        onClick={() => onChange(value + 1)}
        className="rounded border border-border px-1 hover:text-accent"
      >
        <Plus size={10} />
      </button>
    </span>
  );
}

function AddUnitForm({ onAdd }: { onAdd: (input: CreateUnitInput) => void }) {
  const [callsign, setCallsign] = useState('');
  const [type, setType] = useState<ApplianceType>('tanker-cat1');
  const [agency, setAgency] = useState('NSW RFS');
  const [station, setStation] = useState<BrigadeStation | null>(null);

  const handleStationSelect = (s: BrigadeStation) => {
    setStation(s);
    setAgency(s.agency || 'NSW RFS');
    // Convenience default only — real appliance callsign numbering isn't in the seed
    // data, so this pre-fills the station name and leaves it fully editable rather
    // than asserting it as the actual callsign. Never overwrites an in-progress entry.
    if (!callsign.trim()) setCallsign(s.name);
  };

  return (
    <form
      className="space-y-2 border-t border-border pt-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!callsign.trim()) return;
        onAdd({ callsign: callsign.trim(), type, agency, brigade: station?.name, brigadeStationId: station?.id });
        setCallsign('');
        setStation(null);
      }}
    >
      {/* NSW RFS is the only agency with a real, seeded station dataset (the statewide
          facilities list already used for response coverage) — the picker only appears
          for it rather than offering a fabricated list for other agencies. */}
      {agency === 'NSW RFS' && (
        <Field label="NSW RFS station (optional — autofills brigade + a callsign suggestion)">
          <StationPicker stations={allStations()} onSelect={handleStationSelect} />
          {station && (
            <div className="mt-1 flex items-center gap-1.5 text-[11px] text-accent">
              <span>{station.name}</span>
              <button
                type="button"
                onClick={() => setStation(null)}
                aria-label="Clear selected station"
                className="text-muted hover:text-crit"
              >
                <X size={11} />
              </button>
            </div>
          )}
        </Field>
      )}
      <Field label="Add unit — callsign">
        <TextInput value={callsign} onChange={setCallsign} placeholder="e.g. Bungendore 1" ariaLabel="New unit callsign" />
      </Field>
      <div className="flex gap-2">
        <select
          aria-label="Appliance type"
          value={type}
          onChange={(e) => setType(e.target.value as ApplianceType)}
          className="min-w-0 flex-1 rounded-md border border-border bg-surface-2 px-2 py-1.5 text-sm"
        >
          {typeOpts.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Agency"
          value={agency}
          onChange={(e) => {
            setAgency(e.target.value);
            setStation(null);
          }}
          className="min-w-0 flex-1 rounded-md border border-border bg-surface-2 px-2 py-1.5 text-sm"
        >
          <option>NSW RFS</option>
          <option>FRNSW</option>
          <option>NSW Ambulance</option>
          <option>NSW Police</option>
        </select>
        <button
          type="submit"
          aria-label="Add unit"
          className="shrink-0 rounded-md border border-border px-2 text-muted hover:border-accent/60 hover:text-accent"
        >
          <Plus size={16} />
        </button>
      </div>
    </form>
  );
}

function FeedStatusBadge({ roster }: { roster: MemberRoster }) {
  const active = activeMembers(roster);
  const label = roster.source === 'integration' ? 'Live roster' : 'Synthetic roster';
  const age = roster.lastUpdatedAt
    ? new Date(roster.lastUpdatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;
  return (
    <div className="flex items-center gap-1.5 rounded-md border border-accent/30 bg-accent/10 px-2 py-1 text-[11px] text-accent">
      <Radio size={11} aria-hidden />
      <span className="font-semibold">{label}</span>
      <span className="opacity-70">·</span>
      <span>{active.length} active members</span>
      {age && <><span className="opacity-70">·</span><span className="opacity-70">{age}</span></>}
    </div>
  );
}

function SummaryChip({ label, value, tone, detail }: { label: string; value: string; tone?: 'ok' | 'warn'; detail?: string }) {
  const cls = tone === 'warn' ? 'text-high border-high/40' : tone === 'ok' ? 'text-ok border-ok/40' : 'text-muted border-border';
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border bg-surface-2 px-1.5 py-0.5 text-[11px] ${cls}`}>
      <span className="uppercase tracking-wide opacity-70">{label}</span>
      <span className="font-mono">{value}</span>
      {detail && <span className="opacity-60 normal-case not-italic">{detail}</span>}
    </span>
  );
}
